import { useEffect, useState } from 'react';
import styles from './TemporizadorVotacion.module.css';

// Temporizador VISUAL de una votación. Solo se ve en el navegador del admin que
// abrió la votación: la hora de fin vive en localStorage (no hay backend) y NUNCA
// cierra nada; al llegar a cero solo avisa y el admin decide cerrar o dar más tiempo.
// localStorage puede fallar (ventana privada, datos bloqueados): todo va en try/catch
// y sin él la votación se abre y se cierra igual, simplemente sin temporizador.

const PREFIJO = 'eventify.temporizador.';
const CLAVE_ULTIMA = PREFIJO + 'ultima';
const EVENTO = 'eventify-temporizador';
const UN_DIA = 24 * 3600e3;
export const MAX_MINUTOS = 180;

const leer = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const escribir = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* sin almacenamiento: sin temporizador */ } };
const borrar = (k) => { try { window.localStorage.removeItem(k); } catch { /* idem */ } };
const avisar = () => { try { window.dispatchEvent(new Event(EVENTO)); } catch { /* idem */ } };

export function leerFin(preguntaId) {
  const n = Number(leer(PREFIJO + preguntaId));
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Borra los temporizadores viejos (pregunta cerrada por otra vía o asamblea ya terminada).
function limpiarViejos() {
  try {
    const ahora = Date.now();
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIJO) && k !== CLAVE_ULTIMA) {
        const fin = Number(window.localStorage.getItem(k));
        if (!Number.isFinite(fin) || fin < ahora - UN_DIA) window.localStorage.removeItem(k);
      }
    }
  } catch { /* idem */ }
}

export function fijarTemporizador(preguntaId, minutos) {
  const m = Number(minutos);
  if (!Number.isInteger(m) || m < 1 || m > MAX_MINUTOS) return quitarTemporizador(preguntaId);
  limpiarViejos();
  escribir(PREFIJO + preguntaId, String(Date.now() + m * 60e3));
  avisar();
}

export function quitarTemporizador(preguntaId) {
  borrar(PREFIJO + preguntaId);
  avisar();
}

// Da más tiempo: suma desde el fin actual, o desde ahora si ya se cumplió.
export function extenderTemporizador(preguntaId, minutos) {
  const base = Math.max(leerFin(preguntaId) || 0, Date.now());
  escribir(PREFIJO + preguntaId, String(base + minutos * 60e3));
  avisar();
}

// Última duración elegida (0 = sin temporizador), para proponerla la próxima vez.
export function ultimaDuracion() {
  const n = Number(leer(CLAVE_ULTIMA));
  return Number.isInteger(n) && n >= 0 && n <= MAX_MINUTOS ? n : 0;
}
export const guardarUltimaDuracion = (minutos) => escribir(CLAVE_ULTIMA, String(Number(minutos) || 0));

const dosDigitos = (n) => String(n).padStart(2, '0');
const mmss = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${dosDigitos(Math.floor(s / 60))}:${dosDigitos(s % 60)}`;
};

// variante: 'chip' (listas), 'panel' (tarjeta del admin), 'proyeccion' (pantalla de la sala).
// conAcciones: muestra «+1 min» / «+3 min» cuando quedan ≤ 30 s o ya se cumplió.
export default function TemporizadorVotacion({ preguntaId, variante = 'chip', conAcciones = false }) {
  const [fin, setFin] = useState(() => leerFin(preguntaId));
  const [ahora, setAhora] = useState(() => Date.now());

  // Se mantiene al día con otros componentes de la misma pestaña y con otras pestañas.
  useEffect(() => {
    const releer = () => setFin(leerFin(preguntaId));
    releer();
    window.addEventListener(EVENTO, releer);
    window.addEventListener('storage', releer);
    return () => { window.removeEventListener(EVENTO, releer); window.removeEventListener('storage', releer); };
  }, [preguntaId]);

  useEffect(() => {
    if (!fin) return undefined;
    setAhora(Date.now());
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [fin]);

  if (!fin) return null;

  const resto = fin - ahora;
  const cumplido = resto <= 0;
  const poco = !cumplido && resto <= 30e3;
  const grande = variante !== 'chip';
  const proyeccion = variante === 'proyeccion';

  return (
    <div className={proyeccion ? styles.conjuntoProyeccion : styles.conjunto}>
      <span
        role={cumplido ? 'status' : 'timer'}
        aria-label={cumplido ? 'Tiempo cumplido' : `Tiempo restante ${mmss(resto)}`}
        className={`${styles.pildora} ${proyeccion ? styles.tamProyeccion : grande ? styles.tamGrande : styles.tamChip} ${cumplido ? styles.estCumplido : poco ? styles.estPoco : styles.estNormal}`}>
        <i className={`fas ${cumplido ? 'fa-bell' : 'fa-stopwatch'} ${proyeccion ? styles.relojProyeccion : styles.reloj}`} />
        {cumplido ? 'Tiempo cumplido' : mmss(resto)}
      </span>
      {conAcciones && (cumplido || poco) && (
        <span className={styles.acciones}>
          {cumplido && grande && <span className={styles.pista}>Cierra la votación o da más tiempo:</span>}
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => extenderTemporizador(preguntaId, 1)}>+1 min</button>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => extenderTemporizador(preguntaId, 3)}>+3 min</button>
        </span>
      )}
    </div>
  );
}
